import { useState } from "react";
import { PromptTemplateVar, transcribe } from "../../../api";
import { EditVarsModalContentProps, ProcessVarProps, UtilizeModalContentProps, VarElementFactory } from "./VarElementFactory";
import { ChoiceGroup, DefaultButton, IChoiceGroupOption, IconButton, Label, PrimaryButton, Stack, Text, TextField } from "@fluentui/react";
import { isString, parseInt } from "lodash-es";

const EditVarsModalContent_AddTranscribeFile = (props: EditVarsModalContentProps) => {
    const {focusElement, vars, onVarsChanged, backToList} = props
        const [name, setName] = useState(focusElement?.name ?? "")
        const [required, setRequired] = useState<string>(focusElement ? (focusElement?.is_required ? "required" : "not_required") : "")

    const options: IChoiceGroupOption[] = [
        { key: 'required', text: '必須' },
        { key: 'not_required', text: '任意' },
    ];
    const opeType = focusElement ? "修正" : "追加"

    return (
        <form onSubmit={(e) => {
            if (!e.currentTarget.checkValidity()) {
              return
            }
            e.preventDefault()
            const newVar: PromptTemplateVar = {
                id: focusElement?.id ?? window.crypto.randomUUID(),
                name: name,
                description: "",
                is_required: required === "required",
                input_type: "TranscribeFile",
                elements: []
            }
            // 修正
            if(focusElement){
                const newVars = vars.map(v => v.id === newVar.id ? newVar : v)
                onVarsChanged(newVars)
            }
            // 追加
            else {
                const newVars = [...vars]
                newVars.push(newVar)
                onVarsChanged(newVars)
            }
            backToList()
        }}>
            <Stack horizontal horizontalAlign="space-between" verticalAlign="center" id="editvarsmodalcontent-header">
                <h2>
                  {`質問項目の${opeType} (音声ファイル書き起こし)`}
                </h2>
                <IconButton
                  iconProps={{iconName: 'Cancel'}}
                  onClick={backToList}
                />
            </Stack>
            <Stack id="editvarsmodalcontent-body" tokens={{childrenGap: "15px"}} style={{maxHeight: "70vh", minHeight: "70px", overflowY: "auto"}}>
                <TextField 
                    label="質問内容" 
                    style={{width: "100%"}} 
                    maxLength={1024}
                    required 
                    placeholder="例：録音したファイルをアップロードしてください"
                    value={name}
                    onChange={(e) => setName(e.currentTarget.value)}
                />
                <ChoiceGroup 
                    options={options} 
                    label="入力条件" 
                    required 
                    selectedKey={required}
                    onChange={(_, option) => setRequired(option?.key ?? "") }/>
                <Stack horizontal tokens={{childrenGap: "26px"}} style={{marginTop: "20px", marginBottom: "20px"}}>
                    <PrimaryButton text={opeType} type="submit" style={{width: "120px"}} />
                    <DefaultButton text="キャンセル" style={{width: "120px"}} onClick={backToList} />
                </Stack>
            </Stack>
        </form>
    )
}

const getMeetingMaxSpeakersName = (v: PromptTemplateVar) => `${v.id}_maxSpeakers`
const getMeetingDateTimeName = (v: PromptTemplateVar) => `${v.id}_meetingDateTime`

function tryParseNumber(value: string | undefined | null): number | undefined {
    if (value === undefined || value === null) {
        return undefined
    }
    if (value.trim().length == 0) {
        return undefined
    }
    try{
        const ret = Number(value)
        if (Number.isNaN(ret)) {
            return undefined
        }
        return ret
    }
    catch {
        return undefined
    }
}

function tryParseDate(value: string | undefined | null): Date | undefined {
    if (value === undefined || value === null) {
        return undefined
    }
    if (value.trim().length == 0) {
        return undefined
    }
    try{
        const ret = new Date(value)
        if (Number.isNaN(ret.getTime())) {
            return undefined
        }
        return ret
    }
    catch {
        return undefined
    }
}

const VarElement_TranscribeFile = (props: UtilizeModalContentProps) => {
    const { templateVar } = props
    return (<Stack style={{
        backgroundColor: "#f3f2f1",
        borderRadius: "4px",
        padding: "10px",
      }} tokens={{childrenGap: "20px"}}>
      <Stack horizontal verticalAlign="center" tokens={{childrenGap: "20px"}}>
        <Label>{templateVar.name + (templateVar.is_required ? " *" : "")}</Label>
        <Text style={{fontSize: "12px", color: "#605e5c"}}>送信すると書き起こしを開始します</Text>
      </Stack>
      <input type="file" name={templateVar.id} style={{marginTop: "0px"}} required={templateVar.is_required} />

      {/* 発話人数 */}
      <Stack>
        <Stack horizontal>
            <span style={{ fontWeight: "bold", marginRight: "10px" }}>
                発話人数
            </span>
            <span style={{ color: "rgb(96, 94, 92)", fontSize: "12px" }}>
                ※任意
            </span>
        </Stack>
        <div style={{ width: "56px", marginTop: "4px" }}>
            <TextField
                name={getMeetingMaxSpeakersName(templateVar)}
                type="number"
                min="2"
                width={"56px"}
            />
        </div>
        <p style={{ color: "rgb(96, 94, 92)", fontSize: "12px" }}>指定した場合、話者を識別します</p>
      </Stack>

      {/* 会議の日時 */}
      <span>
          <span style={{ fontWeight: "bold", marginRight: "10px" }}>
              会議の日時
          </span>
          <span style={{ color: "rgb(96, 94, 92)", fontSize: "12px" }}>
              ※任意
          </span>
      </span>
      <div style={{ width: "160px", marginTop: "4px" }}>
          <TextField
              name={getMeetingDateTimeName(templateVar)}
              type="datetime-local"
          />
      </div>
    </Stack>)
}

const processTranscribeFileVar = async (props: ProcessVarProps) => {
    const {requestPrompt, templateVar, onChangeRequestPrompt, element, formData} = props

    const maxSpeakers = formData.get(getMeetingMaxSpeakersName(templateVar))
    const maxSpeakersNum = tryParseNumber(maxSpeakers as string | null)
    const meetingDatetime = formData.get(getMeetingDateTimeName(templateVar))
    let meetingDatetimeDt = tryParseDate(meetingDatetime as string | null)
    
    const results: string[] = []
    if (element && element.files) {
      for (const file of element.files) {
        const result = await transcribe(file, maxSpeakersNum, meetingDatetimeDt)
        if ("result" in result) {
            results.push(result.result)
        }
        else {
            throw new Error(result.error)
        }
      }
    }
    const newReq = requestPrompt.replaceAll(`{{${templateVar.id}}}`, results.join("\n\n"))
    onChangeRequestPrompt(newReq)
}

export const TranscribeFileVarElementFactory: VarElementFactory = {
    inputType: "TranscribeFile" as const,
    itemName: "音声ファイル書き起こし入力",
    isDroppableToRequest: true,
    iconName: "EditNote",
    editVarsModalContent: EditVarsModalContent_AddTranscribeFile,
    utilizeModalContent: VarElement_TranscribeFile,
    processVar: processTranscribeFileVar,
    processingMessage: "音声書き起こし中..."
}